import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Plane, Hotel, Train, Bus, Car, Home, Package, MapPin,
  FileText, Ship, Banknote, ShieldCheck, Tag, Globe, Calendar, LogOut, Menu,
  Building2, ToggleRight, Layers, Sparkles, Wallet, User, Receipt, Users,
  BarChart3, X, Activity, ChevronDown, UserCircle2, Settings as SettingsIcon,
  KeyRound, Gauge,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ENTITY_KEYS, ENTITY_SCHEMAS } from './entitySchemas';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';

const ICONS = {
  offers: Tag, destinations: Globe, flights: Plane, hotels: Hotel, trains: Train,
  buses: Bus, cabs: Car, villas: Home, packages: Package, tours: MapPin,
  visas: FileText, cruises: Ship, forex: Banknote, insurance: ShieldCheck,
};

const ENTITY_MODULE = {
  offers: 'marketing',     destinations: 'cms',     flights: 'flights',  hotels: 'hotels',
  trains: 'trains',        buses: 'buses',          cabs: 'cabs',        villas: 'hotels',
  packages: 'holidays',    tours: 'holidays',       visas: 'visa',       cruises: 'holidays',
  forex: 'forex',          insurance: 'insurance',
};

const NAV_PRIMARY = [
  { to: '/admin',                   end: true, label: 'Dashboard',  icon: LayoutDashboard, testid: 'admin-nav-dashboard',       module: 'dashboard' },
  { to: '/admin/bookings',          label: 'Bookings',              icon: Calendar,        testid: 'admin-nav-bookings',        module: 'bookings' },
  { to: '/admin/invoices',          label: 'Invoices',              icon: Receipt,         testid: 'admin-nav-invoices',        module: 'accounting' },
  { to: '/admin/customers',         label: 'Customers',             icon: Users,           testid: 'admin-nav-customers',       module: 'customers' },
  { to: '/admin/corporates',        label: 'Corporates',            icon: Building2,       testid: 'admin-nav-corporates',      module: 'customers' },
  { to: '/admin/crm-analytics',     label: 'CRM Analytics',         icon: BarChart3,       testid: 'admin-nav-crm-analytics',   module: 'reports' },
  { to: '/admin/travel-partners',   label: 'Travel Partners',       icon: Building2,       testid: 'admin-nav-travel-partners', module: 'travel_partners' },
  { to: '/admin/ratehawk',          label: 'RateHawk (ETG)',        icon: Globe,           testid: 'admin-nav-ratehawk',        module: 'suppliers' },
  { to: '/admin/etg-operations',    label: 'ETG Operations',        icon: Gauge,           testid: 'admin-nav-etg-operations',  module: 'suppliers' },
  { to: '/admin/modules',           label: 'Service Modules',       icon: ToggleRight,     testid: 'admin-nav-modules',         module: 'settings' },
  { to: '/admin/comparison',        label: 'Comparison Engine',     icon: Layers,          testid: 'admin-nav-comparison',      module: 'api_management' },
  { to: '/admin/themes',            label: 'Travel Themes',         icon: Sparkles,        testid: 'admin-nav-themes',          module: 'cms' },
  { to: '/admin/payment-gateways',  label: 'Payment Gateways',      icon: Wallet,          testid: 'admin-nav-pg',              module: 'payments' },
  { to: '/admin/analytics',         label: 'Analytics',             icon: Tag,             testid: 'admin-nav-analytics',       module: 'reports' },
  { to: '/admin/users',             label: 'Users & Roles',         icon: User,            testid: 'admin-nav-users',           module: 'user_management' },
  { to: '/admin/system-health',     label: 'System Health',         icon: Activity,        testid: 'admin-nav-system-health',   module: 'audit_logs' },
  { to: '/admin/settings',          label: 'Pricing & Markup',      icon: Tag,             testid: 'admin-nav-settings',        module: 'pricing_rules' },
];

const initialsOf = (s) => {
  if (!s) return 'AD';
  const parts = String(s).split(/[@. _-]+/).filter(Boolean);
  return (parts[0]?.[0] || 'A').toUpperCase() + (parts[1]?.[0] || parts[0]?.[1] || 'D').toUpperCase();
};

const prettyRole = (r) => (r ? r.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : 'Admin');

// ─────────────────────────────────────────────────────────────────────────────
// Profile dropdown — shared between desktop top-bar (full label) and mobile
// hamburger header (avatar only). On <md screens the name+role labels collapse
// to keep the bar uncluttered (matches Stripe / HubSpot pattern).
// ─────────────────────────────────────────────────────────────────────────────
const ProfileDropdown = ({ user, role, onLogout, compact = false }) => {
  const initials = initialsOf(user?.name || user?.email);
  const display = user?.name || (user?.email || '').split('@')[0] || 'Admin';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="admin-profile-trigger"
          className="flex items-center gap-2 rounded-full md:rounded-lg pl-1 pr-1 md:pr-3 py-1 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
          aria-label="Open profile menu"
        >
          <Avatar className="h-8 w-8 ring-2 ring-red-500/10">
            <AvatarFallback className="bg-gradient-to-br from-red-600 to-red-700 text-white text-xs font-bold">
              {initials}
            </AvatarFallback>
          </Avatar>
          {!compact && (
            <span className="hidden md:flex flex-col items-start leading-tight">
              <span className="text-[13px] font-semibold text-gray-900 dark:text-white max-w-[140px] truncate">
                {display}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-red-600 dark:text-red-400">
                {prettyRole(role)}
              </span>
            </span>
          )}
          {!compact && <ChevronDown className="hidden md:block h-4 w-4 text-gray-500" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" data-testid="admin-profile-dropdown">
        <DropdownMenuLabel className="font-normal">
          <div className="flex items-center gap-3 py-1">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-gradient-to-br from-red-600 to-red-700 text-white font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{display}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              <p className="text-[10px] uppercase tracking-wider text-red-600 dark:text-red-400 mt-0.5">
                {prettyRole(role)}
              </p>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="profile-menu-my-profile">
          <UserCircle2 className="h-4 w-4 mr-2 text-gray-500" /> My Profile
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="profile-menu-account">
          <SettingsIcon className="h-4 w-4 mr-2 text-gray-500" /> Account Settings
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="profile-menu-change-password">
          <KeyRound className="h-4 w-4 mr-2 text-gray-500" /> Change Password
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onLogout}
          data-testid="admin-logout-btn"
          className="text-red-600 focus:text-red-700 focus:bg-red-50 dark:focus:bg-red-900/30 cursor-pointer"
        >
          <LogOut className="h-4 w-4 mr-2" /> Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const adminRole = (user?.admin_role || '').toLowerCase();
  const isSuper = !user?.admin_role || adminRole === 'super_admin' || adminRole === 'superadmin';
  const role = adminRole || (isSuper ? 'super_admin' : (user?.role || 'admin'));
  const perms = React.useMemo(() => new Set(user?.permissions || []), [user]);
  const can = React.useCallback((mod) => !mod || isSuper || perms.has(mod), [isSuper, perms]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
      isActive
        ? 'bg-red-600 text-white shadow-sm shadow-red-600/20'
        : 'text-gray-300 hover:bg-gray-800/80 hover:text-white'
    }`;

  const visiblePrimary = NAV_PRIMARY.filter((n) => can(n.module));
  const visibleCatalog = ENTITY_KEYS.filter((k) => can(ENTITY_MODULE[k]));

  return (
    <div className="h-screen flex bg-gray-50 dark:bg-gray-950 overflow-hidden">
      {/* ─── Sidebar (logo + nav only — user section removed and moved to top-bar) ─── */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 w-64 bg-gray-900 text-white z-40 transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } flex flex-col h-screen`}
        data-testid="admin-sidebar"
      >
        <div className="px-5 py-5 border-b border-gray-800 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <img src="/elite-travo-logo.png" alt="Elite Travo" className="h-10 w-10 object-contain" />
            <div>
              <p className="font-bold text-base">Elite<span className="text-red-500">Travo</span></p>
              <p className="text-[10px] uppercase tracking-wider text-gray-400">Admin Panel</p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 text-gray-400 hover:text-white"
            aria-label="Close sidebar"
            data-testid="admin-sidebar-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-4 space-y-1"
          data-testid="admin-sidebar-scroll"
        >
          {visiblePrimary.map(({ to, end, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={linkClass}
              data-testid={testid}
              onClick={() => setSidebarOpen(false)}
            >
              <Icon className="h-4 w-4" />
              <span>{label}</span>
            </NavLink>
          ))}

          {visibleCatalog.length > 0 && (
            <>
              <div className="pt-3 pb-1">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 px-3">Catalog</p>
              </div>
              {visibleCatalog.map((key) => {
                const Icon = ICONS[key] || Tag;
                return (
                  <NavLink
                    key={key}
                    to={`/admin/${key}`}
                    className={linkClass}
                    data-testid={`admin-nav-${key}`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{ENTITY_SCHEMAS[key].label}</span>
                  </NavLink>
                );
              })}
            </>
          )}
        </nav>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
        />
      )}

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* ─── Top bar (sticky) — search/breadcrumb space (left) + profile dropdown (right) ─── */}
        <header
          className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-4 lg:px-6 h-14 flex items-center justify-between sticky top-0 z-20 flex-shrink-0"
          data-testid="admin-topbar"
        >
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 -ml-2"
              data-testid="admin-mobile-menu-btn"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5 text-gray-700 dark:text-gray-300" />
            </button>
            <div className="hidden md:block">
              <p className="text-[13px] font-semibold text-gray-900 dark:text-white">
                EliteTravo · Admin Control Center
              </p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                Manage bookings, suppliers, finance and customer success
              </p>
            </div>
            <p className="md:hidden text-sm font-semibold text-gray-900 dark:text-white">Admin</p>
          </div>
          <div className="flex items-center gap-2">
            <ProfileDropdown user={user} role={role} onLogout={handleLogout} />
          </div>
        </header>

        <main
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 sm:p-6 lg:p-8"
          data-testid="admin-content-scroll"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
